import React, { useState, useEffect, Component, ReactNode } from "react";
import { BrowserRouter as Router, Switch, Redirect, Route, RouteComponentProps, useHistory } from "react-router-dom";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { oktaAuthConfig } from "../../../configOkta";
import { Security } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";
import apis from "../../../Providers.Api/apis";
import Layout from "../../Layout/Layout/Layout";
import { appContext } from "../../../AppContext";
import { LogOutReason } from "../../../Providers.Api/Authentication";
import { MenuItemKey } from "../../Layout/Sidebar/MenuItemKey";
import { StaticContext } from "react-router";
import NotFound from "../../../Pages/Public/NotFound";
import PublicRoutes from './PublicRoutes';
import SharedRoutes from "./SharedRoutes";
import AdminRoutes from "./AdminRoutes";
import OneLensRoutes from "./OneLensRoutes";
import FlexRoutes from "./FlexRoutes";
import { IParameter } from "../../../Providers.Api/Parameters/ParameterRepository";
import { StringHelper } from "../../../Common/StringHelper";

export let routes: IRoute[] = [];

export default class Routes extends Component<IProps, IState>
{
    private get auth() { return appContext().authentication; }
    private get local() { return appContext().localStorageProvider; }
    private get appState() { return appContext().state; }
    private string = new StringHelper();

    private publicRoutes = new PublicRoutes();
    private sharedRoutes = new SharedRoutes();
    private adminRoutes = new AdminRoutes();
    private oneLensRoutes = new OneLensRoutes();
    private flexRoutes = new FlexRoutes();

    constructor(props: IProps)
    {
        super(props);
        routes = [
            ...this.publicRoutes.routes,
            ...this.sharedRoutes.routes,
            ...this.adminRoutes.routes,
            ...this.oneLensRoutes.routes,
            ...this.flexRoutes.routes,
        ];
    }

    private restoreOriginalUri(originalUri: string): void
    {
        const history = (this.props as RouteComponentProps).history;
        if (history)
        {
            history.replace(toRelativeUrl(originalUri, window.location.origin + "/auth"));
        }
        else
        {
            window.location.href = window.location.origin + "/auth";
        }
    }

    // todo: nothing is calling this method. is there a bug where an old page title persists when changing route?
    private resetPageTitle(): void
    {
        this.appState.set({ pageTitle: "" });
    }

    private isAuthenticated(): boolean
    {
        let isAuthenticated = true;
        const token = this.local.getToken()
        if (!token)
        {
            isAuthenticated = false;
        }
        return isAuthenticated;
    }

    public render(): JSX.Element
    {
        const oktaAuth = oktaAuthConfig;
        return (
            <Security oktaAuth={oktaAuth} restoreOriginalUri={(_oktaAuth, originalUri) => this.restoreOriginalUri(originalUri)}>
                <Router>
                    <Switch>
                        {
                            routes.map(route =>
                            {
                                const components: JSX.Element[] = [];
                                if (route.legacyPaths)
                                {
                                    const redirects = route.legacyPaths.map(legacyPath => this.renderRedirect(route, legacyPath));
                                    components.push(...redirects);
                                }
                                if (route.component) // todo: is this check needed?
                                {
                                    const page = (route.isPublic ? this.renderPublicRoute(route) : this.renderPrivateRoute(route));
                                    components.push(page);
                                }
                                return components;
                            })
                        }
                        <Route exact path="*">
                            <NotFound />
                        </Route>
                    </Switch>
                </Router>
            </Security>
        );
    }

    private renderRedirect(route: IRoute, legacyPath: ILegacyRoute): JSX.Element
    {
        return (
            <Route
                key={legacyPath.path}
                path={legacyPath.path}
                exact={true}
                render={props => (<Redirect to={this.mapLagacyPathToNewPath(route, legacyPath, props)} />)}
            />
        );
    }

    private mapLagacyPathToNewPath(route: IRoute, legacyPath: ILegacyRoute, props: RouteComponentProps<{ [key: string]: string | undefined }>): string
    {
        let path = route.path;
        let query = '';

        const originalRouteParams = new Map(Object.entries(props.match.params));
        const originalQuery = props.location.search.replace(/^\?/, '');
        const originalQueryParams = new Map(originalQuery.split('&').filter(i => !!i).map(i => [i.split('=')[0], i.split('=')[1]]));
        const allOriginalParams = new Map([...Array.from(originalRouteParams), ...Array.from(originalQueryParams)]);
        const parameterMapWithLowercaseKeys = new Map(Object.entries(legacyPath.parameterMap ?? {}).map(i => [i[0].toLocaleLowerCase(), i[1]]));

        for (const param of Array.from(allOriginalParams))
        {
            const paramAlias = parameterMapWithLowercaseKeys.get(this.string.toConcatenatedLowercase(param[0])) ?? param[0];
            const paramPlaceholder = ':' + paramAlias;
            const paramValue = param[1] ?? '';

            if (path.includes(paramPlaceholder))
            {
                path = path.replace(new RegExp(paramPlaceholder, 'gi'), paramValue); //  assumption that paramAlias conatins no reg-ex chars
            }
            else if (paramAlias != '')
            {
                query = [query, `${paramAlias}=${paramValue}`].filter(i => !!i).join('&');
            }
        }

        const pathAndQuery = [path, query].filter(i => !!i).join('?');
        return pathAndQuery;
    }

    private renderPublicRoute(route: IRoute): JSX.Element
    {
        return (
            <Route
                key={route.path}
                path={route.path}
                exact={true}
                render={(props) =>
                {
                    return (
                        <Layout {...props} isPrivate={false}>
                            <route.component {...props} />
                        </Layout>
                    );
                }}
            />
        );
    }

    private renderPrivateRoute(route: IRoute): JSX.Element
    {
        return (
            <Route
                key={route.path}
                path={route.path}
                exact={true}
                render={props =>
                {
                    const isAuthenticated = this.isAuthenticated();
                    const isAuthorised = route.isAuthorised();

                    if (!isAuthenticated)
                    {
                        this.auth.logOut(LogOutReason.SessionExpired);
                        return <></>;
                    }
                    else if (!isAuthorised)
                    {
                        return <Redirect to={{ pathname: "/norights" }} />;
                    }
                    else
                    {
                        return (
                            <Layout {...props} isPrivate={true}>
                                <route.component {...props} />
                            </Layout>
                        );
                    }
                }}
            />
        );
    }
}

export interface IProps { }
export interface IState { }

export interface IRoute
{
    isPublic?: boolean;
    isAuthorised: () => boolean;
    path: string;
    legacyPaths?: ILegacyRoute[];
    component: React.ComponentType<any>;
    menuItemId?: MenuItemKey;
}

export interface ILegacyRoute
{
    path: string;
    parameterMap?: IRouteParameterMap;
}

export interface IRouteParameterMap
{
    [oldName: string]: string;
}
